import { getSupabase } from './supabase';
import type { ClubNotificationCategory } from './types';

export type NotificationPreferences = Record<ClubNotificationCategory, boolean>;

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  match: true,
  lineup: true,
  training: true,
  result: true,
  news: true,
  club: true,
};

const PREF_KEY = 'ecc_notification_preferences';

export function loadNotificationPreferences(): NotificationPreferences {
  try {
    return { ...DEFAULT_NOTIFICATION_PREFERENCES, ...JSON.parse(localStorage.getItem(PREF_KEY) || '{}') };
  } catch {
    return DEFAULT_NOTIFICATION_PREFERENCES;
  }
}

export function saveNotificationPreferences(preferences: NotificationPreferences) {
  localStorage.setItem(PREF_KEY, JSON.stringify(preferences));
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) return { ok: false, message: 'Notifications are not supported on this device/browser.' };
  const permission = await Notification.requestPermission();
  return { ok: permission === 'granted', message: permission };
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export async function subscribeForPush(teamId: string) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, message: 'Web Push unavailable on this device.' };
  }

  const publicKey = String((import.meta as any).env?.VITE_VAPID_PUBLIC_KEY || '').trim();
  if (!publicKey) return { ok: false, message: 'Push server key not configured yet.' };

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription = existing || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  const client = getSupabase();
  const { data: auth } = await client.auth.getUser();
  if (!auth.user) return { ok: false, message: 'Sign in required.' };

  const payload = subscription.toJSON();
  const { error } = await client.from('push_subscriptions').upsert({
    user_id: auth.user.id,
    team_id: teamId,
    endpoint: payload.endpoint,
    p256dh: payload.keys?.p256dh,
    auth: payload.keys?.auth,
    user_agent: navigator.userAgent,
    active: true,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'endpoint' });

  return error ? { ok: false, message: error.message } : { ok: true, message: 'Push notifications enabled.' };
}
